import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader, Empty, Stat, DataTag } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Plus, Trash, FloppyDisk, Stack, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";

const RULE_DEFAULTS = {
  rsi: { type: "rsi", op: "<", value: 30 },
  macd_hist: { type: "macd_hist", op: ">" },
  price_vs_sma: { type: "price_vs_sma", period: 50, op: ">" },
  sma_cross: { type: "sma_cross", fast: 20, slow: 50, op: ">" },
};
const RULE_LABELS = { rsi: "RSI", macd_hist: "MACD Histogram", price_vs_sma: "Price vs SMA", sma_cross: "SMA Cross" };

export default function Backtest() {
  const [strategies, setStrategies] = useState([]);
  const [mode, setMode] = useState("single");
  const [form, setForm] = useState({ ticker: "AAPL", strategy: "sma_crossover", period: "2y" });
  const [basket, setBasket] = useState("AAPL, MSFT, NVDA, JPM");
  const [rules, setRules] = useState({ entry: [{ ...RULE_DEFAULTS.rsi }], exit: [{ ...RULE_DEFAULTS.rsi, op: ">", value: 70 }] });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveName, setSaveName] = useState("");

  useEffect(() => { api.get("/backtest/strategies").then((r) => setStrategies([...r.data.strategies, { id: "custom", name: "Custom (build your own)", desc: "Combine RSI, MACD and moving-average rules." }])); }, []);

  const isCustom = form.strategy === "custom";
  const customPayload = () => ({ entry: rules.entry, exit: rules.exit });

  const validateCustom = () => {
    if (isCustom && (rules.entry.length === 0 || rules.exit.length === 0)) {
      toast.error("Custom strategy needs at least one entry and one exit rule"); return false;
    }
    return true;
  };

  const run = async () => {
    if (!validateCustom()) return;
    setLoading(true); setResult(null);
    try {
      if (mode === "single") {
        const r = await api.post("/backtest", { ...form, ticker: form.ticker.toUpperCase(), custom_rules: isCustom ? customPayload() : null });
        setResult({ ...r.data, mode: "single" });
      } else {
        const tickers = basket.split(",").map((t) => t.trim().toUpperCase()).filter(Boolean);
        const r = await api.post("/backtest/portfolio", { tickers, strategy: form.strategy, period: form.period, custom_rules: isCustom ? customPayload() : null });
        setResult({ ...r.data, mode: "portfolio" });
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const saveStrategy = async () => {
    if (!validateCustom()) return;
    const name = saveName.trim() || strategies.find((s) => s.id === form.strategy)?.name || "My Strategy";
    try {
      await api.post("/strategies", { name, strategy: form.strategy, custom_rules: isCustom ? customPayload() : null });
      setSaveName("");
      toast.success(`Saved "${name}" — scan it from Alerts`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const m = result?.metrics;
  const beat = m && m.total_return_pct > m.buy_hold_return_pct;

  return (
    <div>
      <div className="flex items-end justify-between">
        <div><div className="label-xs">Strategy Layer</div><h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter">Strategy Backtesting</h1></div>
        <DataTag kind="HYPO" />
      </div>

      {/* Mode toggle */}
      <div className="mt-4 inline-flex border border-border rounded-sm overflow-hidden">
        <button data-testid="mode-single" onClick={() => setMode("single")} className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors duration-200 ${mode === "single" ? "bg-volt text-white" : "text-muted-foreground hover:text-white"}`}><ChartBar size={15} /> Single Ticker</button>
        <button data-testid="mode-portfolio" onClick={() => setMode("portfolio")} className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors duration-200 ${mode === "portfolio" ? "bg-volt text-white" : "text-muted-foreground hover:text-white"}`}><Stack size={15} /> Portfolio Basket</button>
      </div>

      {/* Config */}
      <div className="mt-3 border border-border bg-card p-5 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        {mode === "single" ? (
          <div>
            <div className="label-xs mb-2">Ticker</div>
            <Input data-testid="bt-ticker" value={form.ticker} onChange={(e) => setForm({ ...form, ticker: e.target.value })} className="bg-background rounded-sm mono uppercase" />
            <div className="mono text-[10px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">Stocks (AAPL), crypto (BTC-USD) or forex
              <button type="button" data-testid="bt-fx-ticker" onClick={() => setForm({ ...form, ticker: "EURUSD" })} className="text-volt hover:underline">try EUR/USD</button>
            </div>
          </div>
        ) : (
          <div className="md:col-span-2">
            <div className="label-xs mb-2">Basket (comma-separated, 2–12)</div>
            <Input data-testid="bt-basket" value={basket} onChange={(e) => setBasket(e.target.value)} className="bg-background rounded-sm mono uppercase" />
            <div className="mono text-[10px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">Mix stocks, crypto & forex
              <button type="button" data-testid="bt-crypto-basket" onClick={() => setBasket("BTC-USD, ETH-USD, SOL-USD, LINK-USD")} className="text-volt hover:underline">crypto basket</button>
              <button type="button" data-testid="bt-fx-basket" onClick={() => setBasket("EURUSD, GBPUSD, USDJPY, AUDUSD")} className="text-volt hover:underline">forex basket</button>
            </div>
          </div>
        )}
        <div>
          <div className="label-xs mb-2">Strategy</div>
          <Select value={form.strategy} onValueChange={(v) => setForm({ ...form, strategy: v })}>
            <SelectTrigger data-testid="bt-strategy" className="bg-background rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{strategies.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-xs mb-2">Period</div>
          <Select value={form.period} onValueChange={(v) => setForm({ ...form, period: v })}>
            <SelectTrigger data-testid="bt-period" className="bg-background rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{[["1y", "1 Year"], ["2y", "2 Years"], ["5y", "5 Years"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="run-backtest-btn" onClick={run} disabled={loading} className="bg-volt hover:bg-volt/90 rounded-sm h-10 gap-2"><Play size={16} weight="fill" /> {loading ? "Running…" : "Run Backtest"}</Button>
      </div>

      {/* Custom builder */}
      {isCustom && (
        <div className="mt-3 border border-volt/30 bg-volt/[0.03] p-5" data-testid="custom-builder">
          <div className="label-xs text-volt mb-3">Custom Strategy Builder</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RuleGroup title="Entry (go long when ALL are true)" list={rules.entry} setList={(l) => setRules({ ...rules, entry: l })} kind="entry" />
            <RuleGroup title="Exit (close when ALL are true)" list={rules.exit} setList={(l) => setRules({ ...rules, exit: l })} kind="exit" />
          </div>
        </div>
      )}

      {/* Save strategy */}
      <div className="mt-3 flex items-center gap-2">
        <Input data-testid="save-name" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Name this strategy…" className="bg-card rounded-sm h-9 max-w-[260px]" />
        <Button data-testid="save-strategy-btn" onClick={saveStrategy} variant="outline" className="rounded-sm border-border h-9 gap-2"><FloppyDisk size={15} /> Save for Alerts</Button>
        {strategies.length > 0 && !isCustom && <span className="mono text-[11px] text-muted-foreground">{strategies.find((s) => s.id === form.strategy)?.desc}</span>}
      </div>

      {loading && <Loader label="Simulating strategy" />}
      {!loading && result && (
        <div className="mt-5">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Stat label={result.mode === "portfolio" ? "Basket Return" : "Strategy Return"} value={<span className={m.total_return_pct >= 0 ? "pos" : "neg"}>{m.total_return_pct}%</span>} sub={`Buy&Hold ${m.buy_hold_return_pct}%`} />
            <Stat label="CAGR" value={`${m.cagr_pct}%`} />
            <Stat label="Sharpe" value={m.sharpe_ratio} valueClass={m.sharpe_ratio >= 1 ? "text-signal-green" : ""} />
            <Stat label="Max Drawdown" value={`${m.max_drawdown_pct}%`} valueClass="text-signal-red" />
            <Stat label="Win Rate" value={`${m.win_rate_pct}%`} sub={`${m.num_trades} trades`} />
            <Stat label="Expectancy" value={`${m.expectancy_pct}%`} sub="per trade" />
          </div>

          <div className={`mt-3 border p-3 mono text-xs rounded-sm ${beat ? "border-signal-green/40 bg-signal-green/5 text-signal-green" : "border-signal-yellow/40 bg-signal-yellow/5 text-signal-yellow"}`}>
            {beat ? "▲ Strategy outperformed buy & hold over this window." : "▼ Strategy underperformed buy & hold over this window."}
          </div>

          <div className="mt-4 border border-border bg-card p-5">
            <div className="label-xs mb-4">Equity Curve · {result.mode === "portfolio" ? result.tickers.join(" · ") : result.ticker} · {result.strategy_name}</div>
            <div className="h-[320px] min-h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <LineChart data={result.equity_curve} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A8A93" }} tickFormatter={(d) => d?.slice(0, 7)} minTickGap={50} />
                  <YAxis tick={{ fontSize: 10, fill: "#8A8A93" }} width={55} />
                  <Tooltip contentStyle={{ background: "#141415", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 4, fontSize: 12, fontFamily: "IBM Plex Mono" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="strategy" name="Strategy" stroke="#007AFF" strokeWidth={1.8} dot={false} />
                  <Line type="monotone" dataKey="buy_hold" name="Buy & Hold" stroke="#8A8A93" strokeWidth={1.2} strokeDasharray="4 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {result.mode === "portfolio" ? (
            <div className="mt-4 border border-border bg-card">
              <div className="border-b border-border px-4 py-3 label-xs">Per-Ticker Contribution</div>
              <div className="grid grid-cols-4 gap-2 px-4 py-2 label-xs border-b border-border/50"><span>Ticker</span><span className="text-right">Strategy</span><span className="text-right">Buy & Hold</span><span className="text-right">Trades</span></div>
              {result.per_ticker.map((p) => (
                <div key={p.ticker} className="grid grid-cols-4 gap-2 px-4 py-2.5 border-b border-border/50 text-sm mono">
                  <span className="font-medium">{p.ticker}</span>
                  <span className={`text-right ${p.strategy_return_pct >= 0 ? "pos" : "neg"}`}>{p.strategy_return_pct >= 0 ? "+" : ""}{p.strategy_return_pct}%</span>
                  <span className="text-right text-muted-foreground">{p.buy_hold_return_pct}%</span>
                  <span className="text-right">{p.num_trades}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4 border border-border bg-card">
              <div className="border-b border-border px-4 py-3 label-xs">Trades ({result.trades.length} most recent)</div>
              {result.trades.length === 0 ? <Empty>No trades generated in this window</Empty> : (
                <>
                  <div className="grid grid-cols-5 gap-2 px-4 py-2 label-xs border-b border-border/50"><span>Entry Date</span><span>Exit Date</span><span className="text-right">Entry</span><span className="text-right">Exit</span><span className="text-right">Return</span></div>
                  {result.trades.map((t, i) => (
                    <div key={i} className="grid grid-cols-5 gap-2 px-4 py-2.5 border-b border-border/50 text-sm mono">
                      <span className="text-muted-foreground text-xs">{t.entry_date}</span>
                      <span className="text-muted-foreground text-xs">{t.exit_date}</span>
                      <span className="text-right">${t.entry}</span>
                      <span className="text-right">${t.exit}</span>
                      <span className={`text-right ${t.return_pct >= 0 ? "pos" : "neg"}`}>{t.return_pct >= 0 ? "+" : ""}{t.return_pct}%</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
          <p className="mt-3 mono text-[10px] text-muted-foreground">Hypothetical historical simulation. Past performance does not guarantee future results.</p>
        </div>
      )}
    </div>
  );
}

function RuleGroup({ title, list, setList, kind }) {
  const add = () => setList([...list, { ...RULE_DEFAULTS.rsi }]);
  const remove = (i) => setList(list.filter((_, idx) => idx !== i));
  const update = (i, rule) => setList(list.map((r, idx) => (idx === i ? rule : r)));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="label-xs">{title}</span>
        <button data-testid={`add-${kind}-rule`} onClick={add} className="flex items-center gap-1 text-xs text-volt hover:text-volt/80 transition-colors"><Plus size={13} /> Add</button>
      </div>
      <div className="space-y-2">
        {list.length === 0 && <div className="mono text-[11px] text-muted-foreground">No rules yet.</div>}
        {list.map((rule, i) => <RuleRow key={i} rule={rule} onChange={(r) => update(i, r)} onRemove={() => remove(i)} />)}
      </div>
    </div>
  );
}

function RuleRow({ rule, onChange, onRemove }) {
  const changeType = (type) => onChange({ ...RULE_DEFAULTS[type] });
  const set = (patch) => onChange({ ...rule, ...patch });
  return (
    <div className="flex items-center gap-2 border border-border bg-background p-2 rounded-sm">
      <Sel value={rule.type} onChange={changeType} options={Object.entries(RULE_LABELS)} w="w-[130px]" testid="rule-type" />
      {rule.type === "rsi" && (<>
        <Sel value={rule.op} onChange={(v) => set({ op: v })} options={[["<", "below"], [">", "above"]]} w="w-[90px]" />
        <Input type="number" value={rule.value} onChange={(e) => set({ value: Number(e.target.value) })} className="h-8 w-[70px] bg-card rounded-sm mono" />
      </>)}
      {rule.type === "macd_hist" && (
        <Sel value={rule.op} onChange={(v) => set({ op: v })} options={[[">", "positive"], ["<", "negative"]]} w="w-[120px]" />
      )}
      {rule.type === "price_vs_sma" && (<>
        <Sel value={rule.op} onChange={(v) => set({ op: v })} options={[[">", "above"], ["<", "below"]]} w="w-[90px]" />
        <Sel value={String(rule.period)} onChange={(v) => set({ period: Number(v) })} options={[["20", "SMA20"], ["50", "SMA50"], ["200", "SMA200"]]} w="w-[100px]" />
      </>)}
      {rule.type === "sma_cross" && (<>
        <Sel value={String(rule.fast)} onChange={(v) => set({ fast: Number(v) })} options={[["20", "SMA20"], ["50", "SMA50"]]} w="w-[90px]" />
        <Sel value={rule.op} onChange={(v) => set({ op: v })} options={[[">", "above"], ["<", "below"]]} w="w-[90px]" />
        <Sel value={String(rule.slow)} onChange={(v) => set({ slow: Number(v) })} options={[["50", "SMA50"], ["200", "SMA200"]]} w="w-[100px]" />
      </>)}
      <button onClick={onRemove} className="ml-auto text-muted-foreground hover:text-signal-red transition-colors"><Trash size={15} /></button>
    </div>
  );
}

function Sel({ value, onChange, options, w = "w-full", testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={`h-8 bg-card rounded-sm ${w}`}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}
